import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { canonicalRequest } from '../../pages/unified/CanonicalEditorCommon';
import { formatDate, labelize, StatusChip } from './WorkspaceStatus';
import { WorkspaceEmptyState } from './WorkspaceStates';

const ResourceActivityPanel = ({ resources = [], title = 'Activity', description, pollInterval = 10000, limit = 12 }) => {
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const resourceKey = resources.filter((item) => item?.type && item?.id).map((item) => `${item.type}:${item.id}`).join('|');

  const load = React.useCallback(async ({ silent = false } = {}) => {
    const validResources = resources.filter((item) => item?.type && item?.id);
    if (!validResources.length) {
      setItems([]);
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    setError('');
    const settled = await Promise.allSettled(validResources.map((resource) => {
      const params = new URLSearchParams({ limit: String(limit), resource_type: resource.type, resource_id: resource.id });
      return canonicalRequest(fetchWithAuth, `/activity?${params.toString()}`);
    }));
    const next = [];
    let failures = 0;
    settled.forEach((result) => {
      if (result.status === 'fulfilled') next.push(...(result.value.items || []));
      else failures += 1;
    });
    const unique = Array.from(new Map(next.map((item) => [`${item.kind}:${item.id}`, item])).values())
      .sort((left, right) => new Date(right.updated_at || 0) - new Date(left.updated_at || 0))
      .slice(0, limit);
    setItems(unique);
    if (failures) setError(failures === validResources.length ? 'Activity could not be loaded.' : 'Some related activity could not be loaded.');
    if (!silent) setLoading(false);
  }, [fetchWithAuth, limit, resourceKey]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!pollInterval || !resourceKey) return undefined;
    const handle = window.setInterval(() => load({ silent: true }), pollInterval);
    return () => window.clearInterval(handle);
  }, [load, pollInterval, resourceKey]);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
          <Box>
            <Typography variant="subtitle1">{title}</Typography>
            {description ? <Typography variant="body2" color="text.secondary">{description}</Typography> : null}
          </Box>
          <Button size="small" startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <RefreshIcon />} onClick={() => load()} disabled={loading}>Refresh</Button>
        </Stack>
        <Divider />
        {error ? <Alert severity="warning">{error}</Alert> : null}
        {loading && !items.length ? <Box role="status" sx={{ py: 4, display: 'grid', placeItems: 'center' }}><CircularProgress aria-label="Loading activity" /></Box> : null}
        {!loading && !items.length ? <WorkspaceEmptyState minHeight={160} title="No activity yet" description="Publication workflows and provider operations will appear here." /> : null}
        {items.map((item) => (
          <Box
            key={`${item.kind}-${item.id}`}
            component="button"
            type="button"
            onClick={() => navigate(`/activity?kind=${item.kind}&q=${item.id}`)}
            sx={{ border: 0, bgcolor: 'transparent', color: 'inherit', textAlign: 'left', p: 1.25, borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <TimelineIcon color="action" aria-hidden="true" />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap>{labelize(item.action)}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{labelize(item.provider || item.kind)} · {formatDate(item.updated_at)}</Typography>
              </Box>
              <StatusChip status={item.status} />
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default ResourceActivityPanel;
